import React, {
  useMemo,
  useEffect,
  useState,
  useDeferredValue,
} from 'react';
import {
  useSelector,
  useDispatch,
  connect,
  shallowEqual,
} from 'react-redux';
import _ from 'lodash';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  createTheme,
  ThemeProvider,
  useTheme,
  ListSubheader,
  Grid,
} from '@mui/material';
import ChartMonthly from './ChartMonthly';
import ChartDefault from './ChartDefault';
import TableDepartments from './TableDepartments';
import TableMonthly from './TableMonthly';
import TableWorkFlow from './TableWorkFlow';
import TableDepartmentsMonthly from './TableDepartmentsMonthly';
import { getInitialReportSettings } from '../../actions/apiGetInfo';
import { readableStampSimple, readableStampSimpleMonthYear } from '../../utils/dateFunctions';
import { toggleReportNav } from '../../actions/toggleMenu';
import { lookupWorkFlowById, lookupDepartmentNameById } from '../../actions/lookups';
import { setCurrentReport, setReportTimeRange, setReportStartDate, setReportEndDate, setMonthlyReportDepartments, setMonthlyReportWorkFlows, setReportDealerships, setReportDepartments, setReportWorkFlows, setReportMonths, setReportFilters } from '../../actions/apiSetInfo';
import LoadingSpinner from '../../components/LoadingSpinner/loadingSpinner';
import { departmentRealAlias, ALIAS_ORDER } from './chartUtils';

const ButtonGroup = ({ name, icon, onClick, active }) => (
  <button
    className={active ? 'btn btn-success active pad-top-1 pad-bottom-1 shadow_2' : 'btn btn-default pad-top-1 pad-bottom-1 shadow_2'}
    name={name}
    onClick={onClick}
  >
    <i className={`${icon} float-left`} />
    {name}
  </button>
);

const formatReportType = (reportType) => {
  const match = reportType?.match(/work_flow_(\d{4})_(\d{2})/);
  if (match) {
    const year = match[1].slice(2);
    const month = match[2];
    return `${month}/${year}`;
  }
  return '';
};

const reportDepartmentSignature = departments => (departments || [])
  .filter(option => option.department_type === 'department')
  .map(option => `${option.id}:${option.name}:${option.name_alias}:${option.dealerships}`)
  .join('|');

const sameReportDepartments = (a, b) => reportDepartmentSignature(a) === reportDepartmentSignature(b);

const aggregateReportDataByAlias = (data, aliasFor) => {
  const dealerships = Object.keys(data.yAxis || {});
  const out = {
    yAxis: {}, rawtime_24_5: {}, rawtime_24_6: {}, xCount: {}, xAxis: [], dataId: {}, departmentId: {}, goal: {},
  };
  const aliasOrder = [];
  const seenAlias = new Set();
  const perDealership = {};

  dealerships.forEach((dealership) => {
    const ids = data.departmentId[dealership] || [];
    const acc = new Map(); // alias -> { tw, t5w, t6w, count, goal }
    ids.forEach((id, i) => {
      const alias = aliasFor(id);
      if (!alias) return;
      if (!seenAlias.has(alias)) { seenAlias.add(alias); aliasOrder.push(alias); }
      const count = Number((data.xCount[dealership] || [])[i]) || 0;
      const cur = acc.get(alias) || {
        tw: 0,
        t5w: 0,
        t6w: 0,
        count: 0,
        goal: (data.goal[dealership] || [])[i] || '',
      };
      cur.tw += (Number((data.yAxis[dealership] || [])[i]) || 0) * count;
      cur.t5w += (Number((data.rawtime_24_5[dealership] || [])[i]) || 0) * count;
      cur.t6w += (Number((data.rawtime_24_6[dealership] || [])[i]) || 0) * count;
      cur.count += count;
      acc.set(alias, cur);
    });
    perDealership[dealership] = acc;
  });

  const aliasRank = (a) => {
    const i = ALIAS_ORDER.indexOf((a || '').toLowerCase());
    return i === -1 ? ALIAS_ORDER.length : i;
  };
  aliasOrder.sort((a, b) => aliasRank(a) - aliasRank(b));

  out.xAxis = aliasOrder;
  dealerships.forEach((dealership) => {
    const acc = perDealership[dealership];
    out.yAxis[dealership] = [];
    out.rawtime_24_5[dealership] = [];
    out.rawtime_24_6[dealership] = [];
    out.xCount[dealership] = [];
    out.dataId[dealership] = [];
    out.departmentId[dealership] = [];
    out.goal[dealership] = [];
    aliasOrder.forEach((alias) => {
      const a = acc.get(alias);
      const count = a ? a.count : 0;
      out.yAxis[dealership].push(count > 0 ? a.tw / count : 0);
      out.rawtime_24_5[dealership].push(count > 0 ? a.t5w / count : 0);
      out.rawtime_24_6[dealership].push(count > 0 ? a.t6w / count : 0);
      out.xCount[dealership].push(count);
      out.goal[dealership].push(a ? a.goal : '');
      out.dataId[dealership].push(''); // no single report id for a combined alias
      out.departmentId[dealership].push(alias); // alias label doubles as the key
    });
  });
  return out;
};

const ReportDashboard = ({ lookupWorkFlowById, lookupDepartmentNameById }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [aliasView, setAliasView] = useState(false);
  const [errors, setErrors] = useState({ reportSettingsError: false });
  const globalTheme = useTheme();

  const {
    report_start_date,
    report_end_date,
    report_dealerships,
    report_departments,
    related_installations,
    current_report,
    work_flows,
    dealer_settings,
    report_months,
    monthly_report_departments,
    monthly_report_work_flows,
    report_work_flows,
    report_filters,
    all_filters,
    report_department_data,
    report_time_range,
    report_menu_status,
    installation,
  } = useSelector((state) => ({
    ...state.settings_reports,
    ...state.settings_dealerships,
    ...state.settings_site,
    ...state.settings_nav.menus,
    work_flows: state.settings_departments.work_flows,
    installation: state.settings_dealerships.installation, // Ensure installation is available
  }), shallowEqual);

  const original_departments = useSelector(
    state => state.settings_departments.original_departments,
    sameReportDepartments,
  );

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: { main: '#007bff' },
        },
        components: {
          MuiCheckbox: {
            styleOverrides: {
              root: {
                '&.Mui-checked': { color: '#007bff' },
                '&:hover': { backgroundColor: 'rgba(0, 123, 255, 0.04)' },
              },
            },
          },
        },
      }),
    [globalTheme]
  );

  useEffect(() => {
    dispatch(getInitialReportSettings())
      .then(() => setIsLoading(false))
      .catch((error) => {
        // console.error('Failed to fetch settings:', error);
        setErrors((prev) => ({ ...prev, reportSettingsError: true }));
        setIsLoading(false);
      });
  }, [dispatch]);

  const dealerSettingValue = (name) => {
    const settings = dealer_settings;
    let value = _.sortBy(_.filter(settings, o => o.key === name && o.dealership === installation.subdomain), 'id');
    if (value.length === 0) {
      value = _.sortBy(_.filter(settings, o => o.key === name && (o.dealership === '' || o.dealership === null || o.dealership === undefined)), 'id');
    }
    if (value.length > 0) {
      if (typeof value[0] !== 'undefined' && typeof value[0].value !== 'undefined') {
        // console.log(`DealerSettingValue for ${name} from installation ${installation.subdomain}: ${value[0].value}`);
        return value[0].value;
      }
    }
    // console.warn(`No ${name} found for ${installation.subdomain}, using default`);
    return name === 'dealership_report_hours' ? 'age-24_5' : ''; // Default fallback
  };

  const selectRelatedInstallations = useMemo(() => {
    const selectedDealerships = report_dealerships.split(',').filter(Boolean);
    return related_installations.map((option) => ({
      id: option.id,
      label: option.softname || option.subdomain,
      subdomain: option.subdomain,
      selected: selectedDealerships.includes(option.subdomain),
      dataid: 0,
    }));
  }, [report_dealerships, related_installations]);

  const departmentsSignature = useMemo(
    () => (original_departments || [])
      .filter(option => option.department_type === 'department')
      .map(option => `${option.id}:${option.name}:${option.name_alias}:${option.dealerships}`)
      .join('|'),
    [original_departments],
  );

  const reportDepartmentOptions = useMemo(() => {
    const selectedDealerships = report_dealerships.split(',').filter(Boolean);
    const seen = new Set();
    const options = [];
    (original_departments || [])
      .filter(option => option.department_type === 'department')
      .filter((option) => {
        const deptDealers = (option.dealerships || '').split(',').map(sub => sub.trim());
        return selectedDealerships.some(sub => deptDealers.includes(sub));
      })
      .forEach((option) => {
        if (seen.has(option.name)) return;
        seen.add(option.name);
        options.push({ id: option.id, label: option.name });
      });
    return options;
  }, [report_dealerships, departmentsSignature]);

  const selectDepartments = useMemo(() => {
    const departmentIds = report_departments ? report_departments.split(',').filter(Boolean) : [];
    return reportDepartmentOptions.map(option => ({
      ...option,
      selected: departmentIds.includes(option.id.toString()),
    }));
  }, [report_departments, reportDepartmentOptions]);

  const selectAliases = useMemo(() => {
    const selectedDealerships = report_dealerships.split(',').filter(Boolean);
    const selectedDeptIds = report_departments ? report_departments.split(',').filter(Boolean) : [];
    const aliasToMembers = new Map();
    (original_departments || [])
      .filter(option => option.department_type === 'department')
      .filter((option) => {
        const deptDealers = (option.dealerships || '').split(',').map(sub => sub.trim());
        return selectedDealerships.some(sub => deptDealers.includes(sub));
      })
      .forEach((option) => {
        const label = departmentRealAlias(option);
        if (!label) return;
        if (!aliasToMembers.has(label)) aliasToMembers.set(label, []);
        aliasToMembers.get(label).push(option.id.toString());
      });
    const titleCase = s => s.replace(/\b\w/g, c => c.toUpperCase());
    const options = [];
    ALIAS_ORDER.forEach((alias) => {
      const label = titleCase(alias);
      const members = aliasToMembers.get(label);
      if (members) {
        options.push({
          id: label,
          label,
          members,
          selected: members.some(m => selectedDeptIds.includes(m)),
        });
      }
    });
    return options;
  }, [report_dealerships, report_departments, departmentsSignature]);

  const selectWorkFlows = useMemo(() => {
    const reportWorkFlows = report_work_flows.split(',').filter(Boolean);
    return work_flows.map((option) => ({
      id: option.id,
      label: option.name,
      selected: reportWorkFlows.includes(option.id.toString()),
    }));
  }, [report_work_flows, work_flows]);

  const selectMonthlyWorkFlows = useMemo(() => work_flows.map((option) => ({
      id: option.id,
      label: option.name,
      selected: monthly_report_work_flows == option.id,
    })), [monthly_report_work_flows, work_flows]);

  const selectMonthlyDepartments = useMemo(() => reportDepartmentOptions.map(option => ({
      ...option,
      selected: monthly_report_departments == option.id,
    })), [monthly_report_departments, reportDepartmentOptions]);

  const selectFilters = useMemo(() => {
    const reportFilters = report_filters.split(',').filter(Boolean);
    return all_filters.map((option) => ({
      id: option.id,
      label: option.filter_name,
      selected: reportFilters.includes(option.id.toString()),
    }));
  }, [report_filters, all_filters]);

  const selectTimeRange = useMemo(() => {
    const d = new Date();
    const last = d.getMonth() - 1;
    const second = d.getMonth() - 2;
    const third = d.getMonth() - 3;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const adjustMonth = (month) => (month < 0 ? month + 12 : month);

    const lastMonth = months[adjustMonth(last)];
    const secondMonth = months[adjustMonth(second)];
    const thirdMonth = months[adjustMonth(third)];

    const comparisonArray = [
      { id: '2', name: 'Month to Date', label: 'Month to Date', value: 'Month to Date' },
      { id: '6', name: '90', label: 'Last 90 days', value: 'Last 90 days' },
      { id: '7', name: '60', label: 'Last 60 days', value: 'Last 60 days' },
      { id: '8', name: '30', label: 'Last 30 days', value: 'Last 30 days' },
      { id: '9', name: '14', label: 'Last 14 days', value: 'Last 14 days' },
      { id: '10', name: '7', label: 'Last 7 days', value: 'Last 7 days' },
    ];

    return comparisonArray.map((option) => ({
      id: option.id,
      name: option.name,
      value: option.value,
      label: option.label,
      selected: report_time_range === option.name,
    }));
  }, [report_time_range]);

  const selectTimeRangeMonths = useMemo(() => {
    const d = new Date();
    const currentYear = d.getFullYear();
    const currentMonth = d.getMonth() + 1; // JavaScript months are 0-based
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const getPastMonthYear = (offset) => {
      let year = currentYear;
      let month = currentMonth - offset;
      if (month <= 0) {
        month += 12;
        year -= 1;
      }
      return { year, month: month.toString().padStart(2, '0') };
    };

    const comparisonArray = [
      { id: '1', name: 'Month to Date', label: 'Month to Date', value: 'Month to Date' },
      ...Array.from({ length: 12 }, (_, i) => {
        const { year, month } = getPastMonthYear(i + 1);
        return {
          id: (i + 2).toString(),
          name: `work_flow_${year}_${month}`,
          label: `${months[parseInt(month, 10) - 1]} ${year}`,
          value: `work_flow_${year}_${month}`,
        };
      }),
    ];

    return comparisonArray.map((option) => {
      const selectedMonths = report_months ? report_months.split(',').filter(Boolean) : [];
      return {
        id: option.id,
        name: option.name,
        value: option.value,
        label: option.label,
        selected: selectedMonths.includes(option.value),
      };
    });
  }, [report_months]);

  const selectTimeRangeStepMonths = useMemo(() => {
    const d = new Date();
    const currentYear = d.getFullYear();
    const currentMonth = d.getMonth() + 1; // JavaScript months are 0-based
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const getPastMonthYear = (offset) => {
      let year = currentYear;
      let month = currentMonth - offset;
      if (month <= 0) {
        month += 12;
        year -= 1;
      }
      return { year, month: month.toString().padStart(2, '0') };
    };

    const comparisonArray = [
      { id: '1', name: 'Month to Date', label: 'Month to Date', value: 'Month to Date' },
      ...Array.from({ length: 12 }, (_, i) => {
        const { year, month } = getPastMonthYear(i + 1);
        return {
          id: (i + 2).toString(),
          name: `step_${year}_${month}`,
          label: `${months[parseInt(month, 10) - 1]} ${year}`,
          value: `step_${year}_${month}`,
        };
      }),
    ];

    return comparisonArray.map((option) => {
      const selectedMonths = report_months ? report_months.split(',').filter(Boolean) : [];
      return {
        id: option.id,
        name: option.name,
        value: option.value,
        label: option.label,
        selected: selectedMonths.includes(option.value),
      };
    });
  }, [report_months]);

  const selectCurrentReport = useMemo(() => {
    const reports = [
      { id: '1', name: 'Step Averages', label: 'Step Averages' },
      { id: '2', name: 'Step Averages (monthly)', label: 'Step Averages (monthly)' },
      { id: '3', name: 'WorkFlow Averages', label: 'WorkFlow Averages' },
      { id: '4', name: 'WorkFlow Averages (monthly)', label: 'WorkFlow Averages (monthly)' },
    ];

    return reports.map((option) => ({
      id: option.id,
      label: option.label,
      selected: current_report === option.label,
      value: option.label,
    }));
  }, [current_report]);

  const reportTitle = useMemo(() => {
    if (current_report === 'WorkFlow Averages (monthly)') {
      return `${current_report} | ${lookupWorkFlowById(monthly_report_work_flows)?.name || ''}`;
    } else if (current_report === 'Step Averages (monthly)') {
      return `${current_report} | ${lookupDepartmentNameById(monthly_report_departments) || ''}`;
    } else if (current_report === 'Step Averages' || current_report === 'WorkFlow Averages') {
      return `${current_report} | ${readableStampSimple(report_start_date)} - ${readableStampSimple(report_end_date)}`;
    }
    return '';
  }, [
    current_report,
    monthly_report_work_flows,
    monthly_report_departments,
    report_start_date,
    report_end_date,
    lookupWorkFlowById,
    lookupDepartmentNameById,
  ]);

  const handleTimeRangeChange = (value) => {
    dispatch(setReportTimeRange(value));

    const name = selectTimeRange.find(option => option.value === value)?.name;
    if (!name) return;

    const date = new Date();
    const dateFirst = new Date();
    let lastDigit;

    if (name === 'last') {
      dateFirst.setMonth(date.getMonth() - 1);
      dateFirst.setDate(1);
      lastDigit = new Date(date.getFullYear(), date.getMonth(), 0);
    } else if (name === 'second') {
      dateFirst.setMonth(date.getMonth() - 2);
      dateFirst.setDate(1);
      lastDigit = new Date(date.getFullYear(), date.getMonth() - 1, 0);
    } else if (name === 'third') {
      dateFirst.setMonth(date.getMonth() - 3);
      dateFirst.setDate(1);
      lastDigit = new Date(date.getFullYear(), date.getMonth() - 2, 0);
    } else if (['90', '60', '30', '14', '7'].includes(name)) {
      const days = parseInt(name, 10);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const start = `${startDate.getFullYear()}-${(`0${startDate.getMonth() + 1}`).slice(-2)}-${(`0${startDate.getDate()}`).slice(-2)}`;
      const end = `${date.getFullYear()}-${(`0${date.getMonth() + 1}`).slice(-2)}-${(`0${date.getDate()}`).slice(-2)}`;
      dispatch(setReportStartDate(start));
      dispatch(setReportEndDate(end));
      return;
    } else if (name === 'Month to Date') {
      const startDate = new Date();
      startDate.setDate(1);
      const start = `${startDate.getFullYear()}-${(`0${startDate.getMonth() + 1}`).slice(-2)}-${(`0${startDate.getDate()}`).slice(-2)}`;
      const end = `${date.getFullYear()}-${(`0${date.getMonth() + 1}`).slice(-2)}-${(`0${date.getDate()}`).slice(-2)}`;
      dispatch(setReportStartDate(start));
      dispatch(setReportEndDate(end));
      return;
    }

    if (lastDigit) {
      const start = `${dateFirst.getFullYear()}-${(`0${dateFirst.getMonth() + 1}`).slice(-2)}-${(`0${dateFirst.getDate()}`).slice(-2)}`;
      const end = `${dateFirst.getFullYear()}-${(`0${dateFirst.getMonth() + 1}`).slice(-2)}-${lastDigit.getDate()}`;
      dispatch(setReportStartDate(start));
      dispatch(setReportEndDate(end));
    }
  };

  const dropdowns = useMemo(() => {
    const dropdownElements = [];

    // Helper function to handle Select All/Deselect All for Locations
    const handleLocationChange = (value, options) => {
      if (value.includes('select-all')) {
        const allSubdomains = options.map(opt => opt.subdomain);
        dispatch(setReportDealerships(allSubdomains.join(',')));
      } else if (value.includes('deselect-all')) {
        dispatch(setReportDealerships(''));
      } else {
        dispatch(setReportDealerships(Array.isArray(value) ? value.join(',') : ''));
      }
    };

    // Helper function to handle Select All/Deselect All for Steps
    const handleStepsChange = (value, options) => {
      if (value.includes('select-all')) {
        const allIds = options.map(opt => opt.id.toString());
        dispatch(setReportDepartments(allIds.join(',')));
      } else if (value.includes('deselect-all')) {
        dispatch(setReportDepartments(''));
      } else {
        dispatch(setReportDepartments(Array.isArray(value) ? value.join(',') : ''));
      }
    };

    const handleAliasStepsChange = (value) => {
      if (value.includes('select-all')) {
        dispatch(setReportDepartments([...new Set(selectAliases.flatMap(a => a.members))].join(',')));
      } else if (value.includes('deselect-all')) {
        dispatch(setReportDepartments(''));
      } else {
        const labels = Array.isArray(value) ? value : [];
        const memberIds = selectAliases.filter(a => labels.includes(a.id)).flatMap(a => a.members);
        dispatch(setReportDepartments([...new Set(memberIds)].join(',')));
      }
    };

    // Helper function to handle Select All/Deselect All for WorkFlows
    const handleWorkFlowsChange = (value, options) => {
      if (value.includes('select-all')) {
        const allIds = options.map(opt => opt.id.toString());
        dispatch(setReportWorkFlows(allIds.join(',')));
      } else if (value.includes('deselect-all')) {
        dispatch(setReportWorkFlows(''));
      } else {
        dispatch(setReportWorkFlows(Array.isArray(value) ? value.join(',') : ''));
      }
    };

    // Helper function to render truncated selected items with ellipsis
    const renderTruncatedValue = (selected, options) => {
      if (selected.length === 0) return '';
      const maxDisplay = 3;
      const displayItems = selected.slice(0, maxDisplay).map((item) => {
        const option = options.find((opt) => opt.subdomain === item || opt.id.toString() === item);
        return option ? option.label : item;
      });
      return displayItems.join(', ') + (selected.length > maxDisplay ? '...' : '');
    };

    if (current_report === 'WorkFlow Averages (monthly)') {
      const location = (
        <Grid size={{ xs: 12, sm: 6, md: 3}} key="location">
          <div className="col">
            <small>Location</small>
            <br />
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
              <Select
                multiple
                value={report_dealerships.split(',').filter(Boolean)}
                onChange={(e) => handleLocationChange(e.target.value, selectRelatedInstallations)}
                renderValue={(selected) => renderTruncatedValue(selected, selectRelatedInstallations)}
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              >
                <MenuItem value="select-all">
                  <em>Select All</em>
                </MenuItem>
                <MenuItem value="deselect-all">
                  <em>Deselect All</em>
                </MenuItem>
                <ListSubheader>Locations</ListSubheader>
                {selectRelatedInstallations.map((option) => (
                  <MenuItem key={option.id} value={option.subdomain}>
                    <Checkbox
                      checked={report_dealerships.split(',').includes(option.subdomain)}
                    />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      );

      if (related_installations.length !== 1) {
        dropdownElements.push(location);
      }

      dropdownElements.push(
        <Grid size={{ xs: 12, sm: 6, md: 3}} key="work_flows">
          <div className="col">
            <small>WorkFlow</small>
            <br />
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
              <Select
                value={monthly_report_work_flows || ''}
                onChange={(e) => dispatch(setMonthlyReportWorkFlows(e.target.value))}
              >
                <MenuItem value="">None</MenuItem>
                {selectMonthlyWorkFlows.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      );
      dropdownElements.push(
        <Grid size={{ xs: 12, sm: 6, md: 3}} key="work_flow_months">
          <div className="col">
            <small>Report Months</small>
            <br />
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
              <Select
                multiple
                value={selectTimeRangeMonths.filter(o => o.selected).map(o => o.value)}
                onChange={(e) => {
                  const value = Array.isArray(e.target.value) ? e.target.value : [];
                  const ownValues = new Set(selectTimeRangeMonths.map(o => o.value));
                  const preserved = report_months.split(',').filter(Boolean).filter(m => !ownValues.has(m));
                  dispatch(setReportMonths([...preserved, ...value].join(',')));
                }}
                renderValue={(selected) =>
                  selected
                    .map((month) => {
                      const option = selectTimeRangeMonths.find(
                        (opt) => opt.value === month
                      );
                      return option ? option.label : month;
                    })
                    .join(', ')
                }
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              >
                {selectTimeRangeMonths.map((month) => (
                  <MenuItem key={month.id} value={month.value}>
                    <Checkbox
                      checked={month.selected}
                    />
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      );
    } else if (current_report === 'Step Averages (monthly)') {
      const location = (
        <Grid size={{ xs: 12, sm: 6, md: 3}} key="location">
          <div className="col">
            <small>Location</small>
            <br />
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
              <Select
                multiple
                value={report_dealerships.split(',').filter(Boolean)}
                onChange={(e) => handleLocationChange(e.target.value, selectRelatedInstallations)}
                renderValue={(selected) => renderTruncatedValue(selected, selectRelatedInstallations)}
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              >
                <MenuItem value="select-all">
                  <em>Select All</em>
                </MenuItem>
                <MenuItem value="deselect-all">
                  <em>Deselect All</em>
                </MenuItem>
                <ListSubheader>Locations</ListSubheader>
                {selectRelatedInstallations.map((option) => (
                  <MenuItem key={option.id} value={option.subdomain}>
                    <Checkbox
                      checked={report_dealerships.split(',').includes(option.subdomain)}
                    />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      );

      if (related_installations.length !== 1) {
        dropdownElements.push(location);
      }

      dropdownElements.push(
        <Grid size={{ xs: 12, sm: 6, md: 3}} key="steps">
          <div className="col">
            <small>Step</small>
            <br />
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
              <Select
                value={monthly_report_departments || ''}
                onChange={(e) => dispatch(setMonthlyReportDepartments(e.target.value))}
              >
                <MenuItem value="">None</MenuItem>
                {selectMonthlyDepartments.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      );
      dropdownElements.push(
        <Grid size={{ xs: 12, sm: 6, md: 3}} key="step_months">
          <div className="col">
            <small>Report Months</small>
            <br />
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
              <Select
                multiple
                value={selectTimeRangeStepMonths.filter(o => o.selected).map(o => o.value)}
                onChange={(e) => {
                  const value = Array.isArray(e.target.value) ? e.target.value : [];
                  const ownValues = new Set(selectTimeRangeStepMonths.map(o => o.value));
                  const preserved = report_months.split(',').filter(Boolean).filter(m => !ownValues.has(m));
                  dispatch(setReportMonths([...preserved, ...value].join(',')));
                }}
                renderValue={(selected) =>
                  selected
                    .map((month) => {
                      const option = selectTimeRangeStepMonths.find(
                        (opt) => opt.value === month
                      );
                      return option ? option.label : month;
                    })
                    .join(', ')
                }
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              >
                {selectTimeRangeStepMonths.map((month) => (
                  <MenuItem key={month.id} value={month.value}>
                    <Checkbox
                      checked={month.selected}
                    />
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      );
    } else if (current_report === 'Step Averages') {
      const location = (
        <Grid size={{ xs: 12, sm: 6, md: 3}} key="location">
          <div className="col">
            <small>Location</small>
            <br />
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
              <Select
                multiple
                value={report_dealerships.split(',').filter(Boolean)}
                onChange={(e) => handleLocationChange(e.target.value, selectRelatedInstallations)}
                renderValue={(selected) => renderTruncatedValue(selected, selectRelatedInstallations)}
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              >
                <MenuItem value="select-all">
                  <em>Select All</em>
                </MenuItem>
                <MenuItem value="deselect-all">
                  <em>Deselect All</em>
                </MenuItem>
                <ListSubheader>Locations</ListSubheader>
                {selectRelatedInstallations.map((option) => (
                  <MenuItem key={option.id} value={option.subdomain}>
                    <Checkbox
                      checked={report_dealerships.split(',').includes(option.subdomain)}
                    />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      );

      if (related_installations.length !== 1) {
        dropdownElements.push(location);
      }

      const stepOptions = aliasView ? selectAliases : selectDepartments;
      dropdownElements.push(
        <Grid size={{ xs: 12, sm: 6, md: 3}} key="steps">
          <div className="col">
            <small>{aliasView ? 'Aliases' : 'Steps'}</small>
            <br />
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
              <Select
                multiple
                value={stepOptions.filter(opt => opt.selected).map(opt => opt.id.toString())}
                onChange={(e) => (aliasView
                  ? handleAliasStepsChange(e.target.value)
                  : handleStepsChange(e.target.value, selectDepartments))}
                renderValue={(selected) => renderTruncatedValue(selected, stepOptions)}
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              >
                <MenuItem value="select-all">
                  <em>Select All</em>
                </MenuItem>
                <MenuItem value="deselect-all">
                  <em>Deselect All</em>
                </MenuItem>
                <ListSubheader>{aliasView ? 'Aliases' : 'Steps'}</ListSubheader>
                {stepOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id.toString()}>
                    <Checkbox
                      checked={opt.selected}
                    />
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      );
      dropdownElements.push(
        <Grid size={{ xs: 12, sm: 6, md: 3}} key="time_range">
          <div className="col">
            <small>Time Range</small>
            <br />
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
              <Select
                value={report_time_range || ''}
                onChange={(e) => handleTimeRangeChange(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {selectTimeRange.map((option) => (
                  <MenuItem key={option.id} value={option.name}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      );
    } else if (current_report === 'WorkFlow Averages') {
      const location = (
        <Grid size={{ xs: 12, sm: 6, md: 3}} key="location">
          <div className="col">
            <small>Location</small>
            <br />
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
              <Select
                multiple
                value={report_dealerships.split(',').filter(Boolean)}
                onChange={(e) => handleLocationChange(e.target.value, selectRelatedInstallations)}
                renderValue={(selected) => renderTruncatedValue(selected, selectRelatedInstallations)}
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              >
                <MenuItem value="select-all">
                  <em>Select All</em>
                </MenuItem>
                <MenuItem value="deselect-all">
                  <em>Deselect All</em>
                </MenuItem>
                <ListSubheader>Locations</ListSubheader>
                {selectRelatedInstallations.map((option) => (
                  <MenuItem key={option.id} value={option.subdomain}>
                    <Checkbox
                      checked={report_dealerships.split(',').includes(option.subdomain)}
                    />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      );

      if (related_installations.length !== 1) {
        dropdownElements.push(location);
      }

      dropdownElements.push(
        <Grid size={{ xs: 12, sm: 6, md: 3}} key="work_flows">
          <div className="col">
            <small>WorkFlows</small>
            <br />
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
              <Select
                multiple
                value={report_work_flows.split(',').filter(Boolean)}
                onChange={(e) => handleWorkFlowsChange(e.target.value, selectWorkFlows)}
                renderValue={(selected) =>
                  selected
                    .map((id) => {
                      const option = selectWorkFlows.find(
                        (opt) => opt.id.toString() === id
                      );
                      return option ? option.label : id;
                    })
                    .join(', ')
                }
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              >
                <MenuItem value="select-all">
                  <em>Select All</em>
                </MenuItem>
                <MenuItem value="deselect-all">
                  <em>Deselect All</em>
                </MenuItem>
                <ListSubheader>WorkFlows</ListSubheader>
                {selectWorkFlows.map((wf) => (
                  <MenuItem key={wf.id} value={wf.id.toString()}>
                    <Checkbox
                      checked={report_work_flows.split(',').includes(wf.id.toString())}
                    />
                    {wf.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      );
      dropdownElements.push(
        <Grid size={{ xs: 12, sm: 6, md: 3}} key="time_range">
          <div className="col">
            <small>Time Range</small>
            <br />
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
              <Select
                value={report_time_range || ''}
                onChange={(e) => handleTimeRangeChange(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {selectTimeRange.map((option) => (
                  <MenuItem key={option.id} value={option.name}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      );
    }
    return dropdownElements;
  }, [
    current_report,
    report_dealerships,
    report_departments,
    report_work_flows,
    report_time_range,
    report_months,
    monthly_report_work_flows,
    monthly_report_departments,
    selectRelatedInstallations,
    selectMonthlyWorkFlows,
    selectTimeRangeMonths,
    selectMonthlyDepartments,
    selectTimeRangeStepMonths,
    selectDepartments,
    selectAliases,
    aliasView,
    selectWorkFlows,
    selectTimeRange,
    related_installations,
    dispatch,
  ]);

  const deferredReportDealerships = useDeferredValue(report_dealerships);
  const deferredReportDepartments = useDeferredValue(report_departments);
  const deferredReportWorkFlows = useDeferredValue(report_work_flows);
  const deferredReportMonths = useDeferredValue(report_months);
  const deferredMonthlyReportDepartments = useDeferredValue(monthly_report_departments);
  const deferredMonthlyReportWorkFlows = useDeferredValue(monthly_report_work_flows);
  const deferredReportTimeRange = useDeferredValue(report_time_range);
  const deferredCurrentReport = useDeferredValue(current_report);
  const deferredReportDepartmentData = useDeferredValue(report_department_data);

  const reportData = useMemo(() => {
    const report_dealerships = deferredReportDealerships;
    const report_departments = deferredReportDepartments;
    const report_work_flows = deferredReportWorkFlows;
    const report_months = deferredReportMonths;
    const monthly_report_departments = deferredMonthlyReportDepartments;
    const monthly_report_work_flows = deferredMonthlyReportWorkFlows;
    const report_time_range = deferredReportTimeRange;
    const current_report = deferredCurrentReport;
    const report_department_data = deferredReportDepartmentData;

    const data = {
      yAxis: {},
      rawtime_24_5: {},
      rawtime_24_6: {},
      xCount: {},
      xAxis: [],
      dataId: {},
      departmentId: {},
      goal: {},
    };

    const reportDealerships = report_dealerships.split(',').filter(Boolean);
    const reportWorkFlows = report_work_flows.split(',').filter(Boolean);

    let reportType = 'step-30';
    let reportTypeMonthToDate = 'work_flow_Month to Date';

    if (current_report === 'Step Averages') {
      reportType = `step-${report_time_range}`;
    } else if (current_report === 'Step Averages (monthly)') {
      reportTypeMonthToDate = 'step-Month to Date';
      reportType = 'step_monthly';
    } else if (current_report === 'WorkFlow Averages') {
      reportType = `work_flow_${report_time_range}`;
    } else if (current_report === 'WorkFlow Averages (monthly)') {
      reportType = 'work_flow_monthly';
      reportTypeMonthToDate = 'work_flow_Month to Date';
    }

    const dealershipReportHours = dealerSettingValue('dealership_report_hours');
    const departmentIds = report_departments ? report_departments.split(',').filter(Boolean) : [];

    const rowsByDealership = new Map();
    report_department_data.forEach((option) => {
      const list = rowsByDealership.get(option.dealership);
      if (list) {
        list.push(option);
      } else {
        rowsByDealership.set(option.dealership, [option]);
      }
    });

    if (reportType.includes('work_flow_monthly')) {
      reportDealerships.forEach((dealership) => {
        data.xCount[dealership] = [];
        data.yAxis[dealership] = [];
        data.rawtime_24_5[dealership] = [];
        data.rawtime_24_6[dealership] = [];
        data.dataId[dealership] = [];
        data.departmentId[dealership] = [];
        data.goal[dealership] = [];

        (rowsByDealership.get(dealership) || []).forEach((option) => {
          if (
            (report_months.includes(option.report_type) || (reportTypeMonthToDate === option.report_type)) &&
            monthly_report_work_flows !== '' &&
            monthly_report_work_flows == option.work_flow_id &&
            Number(option.vehicle_count) > 0
          ) {
            data.xCount[dealership].push(option.vehicle_count || 0);
            data.xAxis.push(readableStampSimpleMonthYear(option.recon_end_date));
            const rawtime_24_7 = (option.rawtime_24_7 / 24).toFixed(2);
            const rawtime_24_6 = (option.rawtime_24_6 / 24).toFixed(2);
            const rawtime_24_5 = (option.rawtime_24_5 / 24).toFixed(2);
            data.rawtime_24_5[dealership].push(Number(rawtime_24_5));
            data.rawtime_24_6[dealership].push(Number(rawtime_24_6));

            if (dealershipReportHours === 'age-24_7') {
              data.yAxis[dealership].push(Number(rawtime_24_7));
            } else if (dealershipReportHours === 'age-24_6') {
              data.yAxis[dealership].push(Number(rawtime_24_6));
            } else {
              data.yAxis[dealership].push(Number(rawtime_24_5));
            }

            data.dataId[dealership].push(option.id);
            data.departmentId[dealership].push(option.work_flow_id);
            data.goal[dealership].push(option.goal);
          }
        });
      });
    } else if (reportType.includes('work_flow_')) {
      reportDealerships.forEach((dealership) => {
        data.xCount[dealership] = [];
        data.yAxis[dealership] = [];
        data.rawtime_24_5[dealership] = [];
        data.rawtime_24_6[dealership] = [];
        data.dataId[dealership] = [];
        data.departmentId[dealership] = [];
        data.goal[dealership] = [];

        (rowsByDealership.get(dealership) || []).forEach((option) => {
          if (
            reportWorkFlows.includes(option.work_flow_id.toString()) &&
            reportType === option.report_type &&
            Number(option.vehicle_count) > 0
          ) {
            data.xCount[dealership].push(option.vehicle_count || 0);
            data.xAxis.push(lookupWorkFlowById(option.work_flow_id)?.name || '');
            const rawtime_24_7 = (option.rawtime_24_7 / 24).toFixed(2);
            const rawtime_24_6 = (option.rawtime_24_6 / 24).toFixed(2);
            const rawtime_24_5 = (option.rawtime_24_5 / 24).toFixed(2);
            data.rawtime_24_5[dealership].push(Number(rawtime_24_5));
            data.rawtime_24_6[dealership].push(Number(rawtime_24_6));

            if (dealershipReportHours === 'age-24_7') {
              data.yAxis[dealership].push(Number(rawtime_24_7));
            } else if (dealershipReportHours === 'age-24_6') {
              data.yAxis[dealership].push(Number(rawtime_24_6));
            } else {
              data.yAxis[dealership].push(Number(rawtime_24_5));
            }

            data.dataId[dealership].push(option.id);
            data.departmentId[dealership].push(option.work_flow_id);
            data.goal[dealership].push(option.goal);
          }
        });
      });
    } else if (reportType.includes('step_monthly')) {
      reportDealerships.forEach((dealership) => {
        data.xCount[dealership] = [];
        data.yAxis[dealership] = [];
        data.rawtime_24_5[dealership] = [];
        data.rawtime_24_6[dealership] = [];
        data.dataId[dealership] = [];
        data.departmentId[dealership] = [];
        data.goal[dealership] = [];

        (rowsByDealership.get(dealership) || []).forEach((option) => {
          if (
            option.department_type === 'department' &&
            (report_months.includes(option.report_type) || (reportTypeMonthToDate === option.report_type)) &&
            monthly_report_departments == option.department_id &&
            Number(option.vehicle_count) > 0
          ) {
            data.xCount[dealership].push(option.vehicle_count || 0);
            data.xAxis.push(readableStampSimpleMonthYear(option.recon_end_date));
            const rawtime_24_7 = (option.rawtime_24_7 / 24).toFixed(2);
            const rawtime_24_6 = (option.rawtime_24_6 / 24).toFixed(2);
            const rawtime_24_5 = (option.rawtime_24_5 / 24).toFixed(2);
            data.rawtime_24_5[dealership].push(Number(rawtime_24_5));
            data.rawtime_24_6[dealership].push(Number(rawtime_24_6));

            if (dealershipReportHours === 'age-24_7') {
              data.yAxis[dealership].push(Number(rawtime_24_7));
            } else if (dealershipReportHours === 'age-24_6') {
              data.yAxis[dealership].push(Number(rawtime_24_6));
            } else {
              data.yAxis[dealership].push(Number(rawtime_24_5));
            }

            data.dataId[dealership].push(option.id);
            data.departmentId[dealership].push(option.department_id);
            data.goal[dealership].push(option.goal);
          }
        });
      });
    } else {
      reportDealerships.forEach((dealership) => {
        data.xCount[dealership] = [];
        data.yAxis[dealership] = [];
        data.rawtime_24_5[dealership] = [];
        data.rawtime_24_6[dealership] = [];
        data.dataId[dealership] = [];
        data.departmentId[dealership] = [];
        data.goal[dealership] = [];
        (rowsByDealership.get(dealership) || []).forEach((option) => {
          if (
            option.department_type === 'department' &&
            reportType === option.report_type &&
            departmentIds.includes(option.department_id.toString()) &&
            Number(option.vehicle_count) > 0
          ) {
            data.xCount[dealership].push(option.vehicle_count || 0);
            data.xAxis.push(option.department_name || '');
            const rawtime_24_7 = (option.rawtime_24_7 / 24).toFixed(2);
            const rawtime_24_6 = (option.rawtime_24_6 / 24).toFixed(2);
            const rawtime_24_5 = (option.rawtime_24_5 / 24).toFixed(2);
            data.rawtime_24_5[dealership].push(Number(rawtime_24_5));
            data.rawtime_24_6[dealership].push(Number(rawtime_24_6));

            if (dealershipReportHours === 'age-24_7') {
              data.yAxis[dealership].push(Number(rawtime_24_7));
            } else if (dealershipReportHours === 'age-24_6') {
              data.yAxis[dealership].push(Number(rawtime_24_6));
            } else {
              data.yAxis[dealership].push(Number(rawtime_24_5));
            }

            data.dataId[dealership].push(option.id);
            data.departmentId[dealership].push(option.department_id);
            data.goal[dealership].push(option.goal);
          }
        });
      });
    }

    return data;
  }, [
    deferredReportDepartmentData,
    deferredReportDealerships,
    deferredReportWorkFlows,
    deferredCurrentReport,
    deferredReportTimeRange,
    deferredReportMonths,
    deferredMonthlyReportWorkFlows,
    deferredMonthlyReportDepartments,
    deferredReportDepartments,
    dealer_settings,
    installation.subdomain, // Dependency to trigger recalculation on subdomain change
    lookupWorkFlowById,
  ]);

  const departmentAlias = useMemo(() => {
    const map = new Map();
    (original_departments || []).forEach((d) => {
      map.set(String(d.id), departmentRealAlias(d));
    });
    return map;
  }, [departmentsSignature]);

  const isStepReport = current_report === 'Step Averages';

  const displayReportData = useMemo(() => {
    if (!aliasView || !isStepReport) return reportData;
    const aliasFor = id => departmentAlias.get(String(id));
    return aggregateReportDataByAlias(reportData, aliasFor);
  }, [aliasView, isStepReport, reportData, departmentAlias]);

  return (
    <ThemeProvider theme={tableTheme}>
      <div className="container col-md-12 reportbox bg-light mobile-pad-left-2">
        <div className="navbar navbar-expand-lg navbar-light bg-light space-top-1 space-bottom-1">
          <button
            className="navbar-toggler"
            type="button"
            onClick={() => dispatch(toggleReportNav())}
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon" />
          </button>

          <div className={`${report_menu_status ? 'row' : 'collapse row '} navbar-collapse mobile-pad-left-2`}>
            <ul className="navbar-nav mr-auto">
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3}} key="choose_report">
                  <div className="col">
                    <small>Choose Report</small>
                    <br />
                    <FormControl fullWidth sx={{ maxWidth: 300 }}>
                      <Select
                        value={current_report || ''}
                        onChange={(e) => dispatch(setCurrentReport(e.target.value))}
                      >
                        <MenuItem value="">None</MenuItem>
                        {selectCurrentReport.map((option) => (
                          <MenuItem key={option.id} value={option.label}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </Grid>
                {isLoading ? (
                  <Grid size={{xs: 12}}>
                    <LoadingSpinner loading />
                  </Grid>
                ) : errors.reportSettingsError ? (
                  <Grid size={{xs: 12}}>
                    <div className="alert alert-danger">Error loading report settings.</div>
                  </Grid>
                ) : (
                  dropdowns
                )}
              </Grid>
            </ul>
            {isStepReport && (
              <div className="pad-left-1 space-bottom-1">
                <button
                  type="button"
                  className={`btn btn-sm ${aliasView ? 'btn-success' : 'btn-outline-secondary'}`}
                  onClick={() => setAliasView(prev => !prev)}
                >
                  <i className="fal fa-layer-group space-right-1" />
                  {aliasView ? 'Combine by Alias: On' : 'Combine by Alias: Off'}
                </button>
              </div>
            )}
            <div className="row">
              <div className="col-md-5 pad-left-1">
                {report_department_data.length > 0 ? (
                  (() => {
                    switch (current_report) {
                      case 'WorkFlow Averages':
                        return (
                          <ChartDefault
                            key={reportTitle}
                            report_title={reportTitle}
                            data={displayReportData}
                            report_dealerships={deferredReportDealerships}
                            container="chart"
                            current_report={current_report}
                          />
                        );
                      case 'Step Averages':
                        return (
                          <ChartDefault
                            key={reportTitle}
                            report_title={reportTitle}
                            data={displayReportData}
                            report_dealerships={deferredReportDealerships}
                            container="chart"
                            current_report={current_report}
                          />
                        );
                      case 'WorkFlow Averages (monthly)':
                        return (
                          <ChartMonthly
                            key={reportTitle}
                            report_title={reportTitle}
                            data={displayReportData}
                            report_dealerships={deferredReportDealerships}
                            container="chart"
                            current_report={current_report}
                          />
                        );
                      case 'Step Averages (monthly)':
                        return (
                          <ChartDefault
                            key={reportTitle}
                            report_title={reportTitle}
                            data={displayReportData}
                            report_dealerships={deferredReportDealerships}
                            container="chart"
                            current_report={current_report}
                          />
                        );
                      default:
                        return <div>No report available</div>;
                    }
                  })()
                ) : (
                  <div className="row text-center bg-dark">
                    <LoadingSpinner loading />
                  </div>
                )}
              </div>
              <div className="col-md-7 bg-light">
                {report_department_data.length > 0 ? (
                  (() => {
                    switch (current_report) {
                      case 'WorkFlow Averages':
                        return (
                          <TableWorkFlow
                            data={displayReportData}
                            dealership_report_hours={dealerSettingValue('dealership_report_hours')}
                          />
                        );
                      case 'WorkFlow Averages (monthly)':
                        return (
                          <TableMonthly
                            data={displayReportData}
                            dealership_report_hours={dealerSettingValue('dealership_report_hours')}
                          />
                        );
                      case 'Step Averages (monthly)':
                        return (
                          <TableDepartmentsMonthly
                            data={displayReportData}
                            dealership_report_hours={dealerSettingValue('dealership_report_hours')}
                          />
                        );
                      default:
                        return (
                          <TableDepartments
                            data={displayReportData}
                            dealership_report_hours={dealerSettingValue('dealership_report_hours')}
                            report_dealerships={deferredReportDealerships}
                            aliasView={aliasView && isStepReport}
                          />
                        );
                    }
                  })()
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

const mappedActions = {
  toggleReportNav,
  lookupWorkFlowById,
  lookupDepartmentNameById,
};

function mapStateToProps(state) {
  return {};
}

export default connect(mapStateToProps, mappedActions)(ReportDashboard);